import { gsap } from 'gsap'
import Experience from './Experience/Experience.js'

/* ─────────────────────────────────────────────────────────────────
   Window-dimension patch
   ─────────────────────────────────────────────────────────────────
   Sizes.js reads window.innerWidth / window.innerHeight to size the
   WebGL canvas and compute the camera aspect ratio.  We patch those
   two getters so they return the hero-right container's dimensions
   instead of the full viewport — keeping the canvas confined to the
   right half of the hero without touching any restricted files.
──────────────────────────────────────────────────────────────────── */
const heroRight = document.getElementById('hero-right')

function containerW() {
  const w = heroRight.getBoundingClientRect().width
  return w > 0 ? w : document.documentElement.clientWidth / 2
}

function containerH() {
  const h = heroRight.getBoundingClientRect().height
  return h > 0 ? h : document.documentElement.clientHeight
}

try {
  Object.defineProperty(window, 'innerWidth',  { get: containerW, configurable: true })
  Object.defineProperty(window, 'innerHeight', { get: containerH, configurable: true })
} catch (_) {
  // Fallback: some environments disallow patching window properties.
  // The canvas will fall back to full-screen but the page still works.
}

/* ─────────────────────────────────────────────────────────────────
   Boot WebGL Experience
──────────────────────────────────────────────────────────────────── */
const canvas = document.getElementById('webgl-hero')
new Experience(canvas)

/* ─────────────────────────────────────────────────────────────────
   Nav — scroll state
──────────────────────────────────────────────────────────────────── */
const nav = document.getElementById('nav')

window.addEventListener('scroll', () => {
  nav.classList.toggle('scrolled', document.documentElement.scrollTop > 8)
}, { passive: true })

/* ─────────────────────────────────────────────────────────────────
   Hero — entrance animation (GSAP)
   emilkowalski: ease-out, stagger 100ms, 650ms duration.
   Elements start at { opacity:0, translateY:24px } via CSS.
──────────────────────────────────────────────────────────────────── */
const heroSequence = [
  '.hero-badge',
  '.hero-title',
  '.hero-subtitle',
  '.hero-actions',
  '.hero-meta',
]

if (document.documentElement.clientWidth > 768) {
  gsap.to(heroSequence.join(', '), {
    opacity: 1,
    y: 0,
    duration: 0.72,
    ease: 'power3.out',     // ≈ cubic-bezier(0.23, 1, 0.32, 1)
    stagger: 0.1,
    delay: 0.15,
    clearProps: 'transform',
  })
}

/* ─────────────────────────────────────────────────────────────────
   Scroll reveals — IntersectionObserver
   emilkowalski: translateY(20px) → 0, opacity 0 → 1.
   Stagger is handled by CSS transition-delay on nth-child selectors.
──────────────────────────────────────────────────────────────────── */
const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      entry.target.classList.add('revealed')
      revealObserver.unobserve(entry.target)
    })
  },
  { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
)

document.querySelectorAll('.reveal').forEach((el) => revealObserver.observe(el))

/* ─────────────────────────────────────────────────────────────────
   Feature cards — cursor-spotlight effect
   Tracks mouse position per card → CSS custom properties
   that drive the radial-gradient in .feature-card::before.
──────────────────────────────────────────────────────────────────── */
document.querySelectorAll('.feature-card').forEach((card) => {
  card.addEventListener('mousemove', (e) => {
    const rect = card.getBoundingClientRect()
    card.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`)
    card.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`)
  })

  card.addEventListener('mouseleave', () => {
    // Reset to center so ::before fades cleanly
    card.style.setProperty('--mouse-x', '50%')
    card.style.setProperty('--mouse-y', '50%')
  })
})

/* ─────────────────────────────────────────────────────────────────
   CTA form — micro-feedback on submit
──────────────────────────────────────────────────────────────────── */
const ctaForm = document.querySelector('.cta-form')
const ctaBtn  = ctaForm?.querySelector('button')

ctaForm?.addEventListener('submit', (e) => {
  e.preventDefault()
  const input = ctaForm.querySelector('.cta-input')
  if (!input?.value.includes('@')) return

  const original = ctaBtn.textContent
  ctaBtn.textContent = 'You\'re on the list!'
  ctaBtn.disabled = true

  gsap.fromTo(ctaBtn,
    { scale: 0.97 },
    { scale: 1, duration: 0.22, ease: 'power2.out' }
  )

  setTimeout(() => {
    ctaBtn.textContent = original
    ctaBtn.disabled = false
    input.value = ''
  }, 3000)
})
